import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { API_BASE_URL } from '../config/api';
import {
  CheckCircle, XCircle, Clock, FileText, Download, ChevronDown, ChevronUp,
  Building2, User, Mail, Phone, Star, Award, BellRing
} from 'lucide-react';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

export default function AdminMembers() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState('');
  const [acting, setActing] = useState(false);
  const [spotlight, setSpotlight] = useState({ enabled: false, member_id: null });
  const [savingSpotlight, setSavingSpotlight] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    axios.get(`${API_BASE_URL}/api/membership-applications/admin/spotlight`, {
      headers: authHeaders(), withCredentials: true
    }).then(({ data }) => setSpotlight({ enabled: !!data.enabled, member_id: data.member_id || null }))
      .catch(() => {});
  }, [user]);

  const saveSpotlight = async (next) => {
    setSavingSpotlight(true);
    try {
      await axios.post(`${API_BASE_URL}/api/membership-applications/admin/spotlight`, next, {
        headers: authHeaders(), withCredentials: true
      });
      setSpotlight(next);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update spotlight');
    } finally {
      setSavingSpotlight(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    fetchApplications();
  }, [user, authLoading, statusFilter, typeFilter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('member_type', typeFilter);
      const { data } = await axios.get(
        `${API_BASE_URL}/api/membership-applications/admin/list?${params.toString()}`,
        { headers: authHeaders(), withCredentials: true }
      );
      setApplications(data.applications);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const verifyDocument = async (applicationId, documentId, status) => {
    setActing(true);
    try {
      await axios.post(`${API_BASE_URL}/api/membership-applications/admin/verify-document`,
        { application_id: applicationId, document_id: documentId, status, notes: status === 'rejected' ? (notes || 'Document not acceptable') : null },
        { headers: authHeaders(), withCredentials: true });
      await fetchApplications();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const verifyApplication = async (applicationId, status) => {
    setActing(true);
    try {
      await axios.post(`${API_BASE_URL}/api/membership-applications/admin/verify-application`,
        { application_id: applicationId, status, notes: notes || null },
        { headers: authHeaders(), withCredentials: true });
      setNotes('');
      await fetchApplications();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const downloadCertificate = async (app) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/membership-applications/admin/certificate/${app.id}`,
        { headers: authHeaders(), withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CSED_Certificate_${(app.organization_name || app.name).replace(/ /g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
    } catch (e) {
      alert('Certificate download failed');
    }
  };

  const sendRenewalReminder = async (applicationId) => {
    setActing(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/membership-applications/admin/send-renewal-reminder`,
        { application_id: applicationId },
        { headers: authHeaders(), withCredentials: true }
      );
      alert(data.message);
      await fetchApplications();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setActing(false);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/membership-applications/documents/${doc.id}/download`,
        { headers: authHeaders(), withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_name;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
    } catch (e) {
      alert('Download failed');
    }
  };

  const statusBadge = (status) => {
    if (status === 'approved') return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
    if (status === 'rejected') return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
  };

  const docLabel = (key) => ({
    registration_certificate: 'Registration Certificate',
    pan_card: 'PAN Card',
    gst_certificate: 'GST Certificate',
    certificate_12a_80g: '12A / 80G Certificate'
  }[key] || key);

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="admin-members-title">Member Verification</h1>
        <p className="text-gray-600">Review membership applications and verify uploaded documents</p>
      </div>

      {/* Directory Spotlight Control */}
      <Card className="p-5 mb-6" data-testid="spotlight-control-card">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-orange-100 rounded-full"><Star className="h-5 w-5 text-[#E87722]" /></div>
            <div>
              <h3 className="font-bold text-gray-900">Homepage Member Spotlight</h3>
              <p className="text-sm text-gray-500">
                When enabled, a featured member appears on the homepage. Leave the pick on "Rotate automatically"
                and it showcases a random approved member — zero upkeep needed.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={spotlight.member_id || ''}
              onChange={(e) => saveSpotlight({ ...spotlight, member_id: e.target.value || null })}
              className="border rounded-md p-2 bg-white text-sm"
              disabled={savingSpotlight}
              data-testid="spotlight-member-select"
            >
              <option value="">Rotate automatically</option>
              {applications.filter(a => a.status === 'approved').map(a => (
                <option key={a.id} value={a.id}>
                  {a.member_type === 'institutional' ? (a.organization_name || a.name) : a.name}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <Switch
                checked={spotlight.enabled}
                onCheckedChange={(checked) => saveSpotlight({ ...spotlight, enabled: checked })}
                disabled={savingSpotlight}
                data-testid="spotlight-toggle"
              />
              <span className={`text-sm font-medium ${spotlight.enabled ? 'text-green-700' : 'text-gray-500'}`}>
                {spotlight.enabled ? 'Live' : 'Off'}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded-md p-2 bg-white" data-testid="admin-members-status-filter">
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="border rounded-md p-2 bg-white" data-testid="admin-members-type-filter">
          <option value="">All Types</option>
          <option value="individual">Individual</option>
          <option value="institutional">Institutional</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A5F] mx-auto"></div>
        </div>
      ) : applications.length === 0 ? (
        <Card className="p-12 text-center" data-testid="admin-members-empty">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No membership applications found</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => (
            <Card key={app.id} className="overflow-hidden" data-testid={`admin-app-card-${app.id}`}>
              <div
                className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => { setExpanded(expanded === app.id ? null : app.id); setNotes(''); }}
                data-testid={`admin-app-toggle-${app.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${app.member_type === 'institutional' ? 'bg-blue-100' : 'bg-green-100'}`}>
                    {app.member_type === 'institutional'
                      ? <Building2 className="h-5 w-5 text-blue-700" />
                      : <User className="h-5 w-5 text-green-700" />}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">
                      {app.member_type === 'institutional' ? (app.organization_name || app.name) : app.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {app.selected_plan} • Applied {new Date(app.created_at).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {app.member_type === 'institutional' && (
                    <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                      {(app.documents || []).length} document{(app.documents || []).length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                  {statusBadge(app.status)}
                  {expanded === app.id ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                </div>
              </div>

              {expanded === app.id && (
                <div className="border-t px-5 py-6 bg-gray-50" data-testid={`admin-app-detail-${app.id}`}>
                  {/* Applicant Details */}
                  <div className="grid md:grid-cols-2 gap-4 mb-6 text-sm">
                    <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-gray-400" />{app.email}</div>
                    <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400" />{app.phone}</div>
                    {app.member_type === 'institutional' ? (
                      <>
                        <div><span className="text-gray-500">Org Type:</span> {app.organization_type || '—'}</div>
                        <div><span className="text-gray-500">Representative:</span> {app.representing_executive_name || '—'} {app.representing_executive_designation ? `(${app.representing_executive_designation})` : ''}</div>
                        <div><span className="text-gray-500">Business:</span> {app.nature_of_business || '—'}</div>
                        <div><span className="text-gray-500">Employees:</span> {app.num_employees || '—'}</div>
                      </>
                    ) : (
                      <>
                        <div><span className="text-gray-500">Designation:</span> {app.designation || '—'}</div>
                        <div><span className="text-gray-500">Affiliation:</span> {app.organization_affiliation || '—'}</div>
                      </>
                    )}
                    <div className="md:col-span-2"><span className="text-gray-500">Purpose:</span> {app.purpose_category} — {app.purpose_details}</div>
                  </div>

                  {/* Documents */}
                  {app.member_type === 'institutional' && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Verification Documents</h4>
                      {(app.documents || []).length === 0 ? (
                        <p className="text-sm text-gray-500 italic">No documents uploaded yet</p>
                      ) : (
                        <div className="space-y-2">
                          {app.documents.map((doc) => (
                            <div key={doc.id} className="flex items-center justify-between bg-white border rounded-lg p-3 flex-wrap gap-2" data-testid={`admin-doc-row-${doc.doc_type}`}>
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-[#E87722]" />
                                <div>
                                  <p className="font-medium text-sm">{docLabel(doc.doc_type)}</p>
                                  <p className="text-xs text-gray-500">{doc.original_name} • {new Date(doc.uploaded_at).toLocaleDateString('en-IN')}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {statusBadge(doc.status)}
                                <Button size="sm" variant="ghost" onClick={() => downloadDoc(doc)} data-testid={`admin-doc-download-${doc.doc_type}`}>
                                  <Download className="h-4 w-4" />
                                </Button>
                                {doc.status !== 'approved' && (
                                  <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50"
                                    disabled={acting} onClick={() => verifyDocument(app.id, doc.id, 'approved')}
                                    data-testid={`admin-doc-approve-${doc.doc_type}`}>
                                    Approve
                                  </Button>
                                )}
                                {doc.status !== 'rejected' && (
                                  <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50"
                                    disabled={acting} onClick={() => verifyDocument(app.id, doc.id, 'rejected')}
                                    data-testid={`admin-doc-reject-${doc.doc_type}`}>
                                    Reject
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Application-level Actions */}
                  <div className="bg-white border rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Application Decision</h4>
                    <Textarea
                      placeholder="Notes for the applicant (optional)"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="mb-3"
                      data-testid="admin-app-notes"
                    />
                    <div className="flex gap-3 flex-wrap">
                      <Button className="bg-green-600 hover:bg-green-700" disabled={acting || app.status === 'approved'}
                        onClick={() => verifyApplication(app.id, 'approved')} data-testid="admin-app-approve-btn">
                        <CheckCircle className="h-4 w-4 mr-2" />Approve Member
                      </Button>
                      <Button variant="destructive" disabled={acting || app.status === 'rejected'}
                        onClick={() => verifyApplication(app.id, 'rejected')} data-testid="admin-app-reject-btn">
                        <XCircle className="h-4 w-4 mr-2" />Reject
                      </Button>
                      {app.status === 'approved' && (
                        <>
                          <Button variant="outline" disabled={acting}
                            onClick={() => sendRenewalReminder(app.id)}
                            className="border-[#E87722] text-[#E87722] hover:bg-[#E87722] hover:text-white"
                            data-testid="admin-send-renewal-btn">
                            <BellRing className="h-4 w-4 mr-2" />Send Renewal Reminder
                          </Button>
                          <Button variant="outline" disabled={acting}
                            onClick={() => downloadCertificate(app)}
                            className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
                            data-testid="admin-download-certificate-btn">
                            <Award className="h-4 w-4 mr-2" />Certificate
                          </Button>
                        </>
                      )}
                    </div>
                    {app.last_renewal_reminder_at && (
                      <p className="text-xs text-gray-500 mt-2">
                        Last renewal reminder sent: {new Date(app.last_renewal_reminder_at).toLocaleString('en-IN')}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">Approved members appear in the public member directory (visible to logged-in members only).</p>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
